import { Badge, Tooltip, Menu, MenuItem, IconButton, Box, Tabs, Tab, Avatar, Divider, ListItemIcon, Typography, Select, FormControl } from '@mui/material';
import {
    Close as CloseIcon,
    Menu as MenuIcon,
    Notifications as NotificationsIcon,
    Chat as ChatIcon,
    Brightness4 as DarkModeIcon,
    // Logic: mode === 'dark' ? <LightModeIcon /> (Sun) : <DarkModeIcon /> (Moon)
    Brightness7 as LightModeIcon,
    Translate as LanguageIcon,
    Logout,
    Person as PersonIcon,
    Business as BusinessIcon,
    AccountCircle,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    Palette as PaletteIcon,
    HelpOutline as HelpIcon,
    GridView as GridViewIcon,
    FormatListBulleted as TableListIcon,
    Apps as AppsIcon,
} from '@mui/icons-material';
import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTabContext } from '../contexts/TabContext';
import { useLanguage, Language } from '../contexts/LanguageContext';
import { useThemeContext, ViewMode } from '../contexts/ThemeContext';
import { useNotification } from '../contexts/NotificationContext';
import NotificationDrawer from './NotificationDrawer';
import { useChat } from '../contexts/ChatContext';
import ChatDrawer from './Chat/ChatDrawer';
import { ThemeCustomizer } from './ThemeCustomizer';
import { UserManual } from './UserManual';
import { useApplication } from '../contexts/ApplicationContext';

interface TopNavigationTabsProps {
    onToggleSidebar?: () => void;
    isMobile?: boolean;
}

const TopNavigationTabs: React.FC<TopNavigationTabsProps> = ({ onToggleSidebar, isMobile }) => {
    const { tabs, activeTabPath, activateTab, removeTab } = useTabContext();
    const { t, language, setLanguage } = useLanguage();
    const { toggleColorMode, mode, defaultViewMode, setDefaultViewMode } = useThemeContext();
    const { unreadCount } = useNotification();
    const { unreadCount: chatUnreadCount } = useChat();
    const { user, logout } = useAuth();
    const { currentApp, currentAppVersion } = useApplication();

    const [anchorElLang, setAnchorElLang] = React.useState<null | HTMLElement>(null);
    const [anchorElViewMode, setAnchorElViewMode] = React.useState<null | HTMLElement>(null);
    const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
    const [themeAnchorEl, setThemeAnchorEl] = React.useState<null | HTMLElement>(null);
    const [notifOpen, setNotifOpen] = React.useState(false);
    const [chatOpen, setChatOpen] = React.useState(false);
    const [isFullScreen, setIsFullScreen] = React.useState(false);
    const [manualOpen, setManualOpen] = React.useState(false);

    const handleToggleFullScreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    React.useEffect(() => {
        const handleFullScreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullScreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
        };
    }, []);

    const handleLangOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElLang(event.currentTarget);
    };

    const handleViewModeOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElViewMode(event.currentTarget);
    };

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setAnchorElUser(null);
    };

    const handleLogout = async () => {
        handleUserMenuClose();
        await logout();
    };

    const handleSwitchApp = async () => {
        handleUserMenuClose();
        await logout(true);
        window.location.href = '/Externel_apps/index.html';
    };

    const handleManualOpen = () => {
        handleUserMenuClose();
        setManualOpen(true);
    };

    const handleLangClose = () => {
        setAnchorElLang(null);
    };

    const handleViewModeClose = () => {
        setAnchorElViewMode(null);
    };

    const handleChangeLanguage = (lang: Language) => {
        setLanguage(lang);
        handleLangClose();
        handleUserMenuClose();
    };

    const handleChangeViewMode = (newMode: ViewMode) => {
        setDefaultViewMode(newMode);
        handleViewModeClose();
        handleUserMenuClose();
    };

    const handleThemeOpen = (event: React.MouseEvent<HTMLElement>) => {
        setThemeAnchorEl(event.currentTarget);
    };

    const handleThemeClose = () => {
        setThemeAnchorEl(null);
    };

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        activateTab(newValue);
    };

    const handleClose = (e: React.MouseEvent, path: string) => {
        e.stopPropagation();
        removeTab(path);
    };

    return (
        <>
            <Box
                className="no-print"
                sx={{
                // ... (existing Box styles)
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                pt: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pl: isMobile ? 1 : 0,
                pr: 2
            }}>
                {/* ... (existing Mobile Button) */}
                {isMobile && onToggleSidebar && (
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={onToggleSidebar}
                        sx={{ color: 'primary.main' }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}

                {isMobile ? (
                    <FormControl sx={{ flexGrow: 1, mr: 1, maxWidth: 'calc(100vw - 120px)' }}> {/* Constrain width on mobile */}
                        <Select
                            value={activeTabPath || ''}
                            onChange={(e) => activateTab(e.target.value)}
                            variant="standard"
                            disableUnderline
                            displayEmpty
                            renderValue={(selected) => {
                                const selectedTab = tabs.find(tab => tab.path === selected);
                                if (!selectedTab) return null;
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', overflow: 'hidden' }}>
                                        {selectedTab.icon && (
                                            typeof selectedTab.icon === 'string' ?
                                                <i className={selectedTab.icon} style={{ fontSize: '1.2rem', minWidth: '24px' }}></i> :
                                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', minWidth: '24px', '& > svg': { fontSize: '1.2rem' } }}>
                                                    {selectedTab.icon}
                                                </Box>
                                        )}
                                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                            {selectedTab.translationKey ? t(selectedTab.translationKey) : selectedTab.title}
                                        </Typography>
                                    </Box>
                                );
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: { maxHeight: '60vh' }
                                },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                }
                            }}
                            sx={{
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 1.5,
                                    pl: 1,
                                    pr: '24px !important' // Ensure space for arrow
                                }
                            }}
                        >
                            {tabs.map((tab) => (
                                <MenuItem key={tab.path} value={tab.path} divider>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', overflow: 'hidden' }}>
                                        {tab.icon && (
                                            typeof tab.icon === 'string' ?
                                                <i className={tab.icon} style={{ fontSize: '1.2rem', minWidth: '24px' }}></i> :
                                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', minWidth: '24px', '& > svg': { fontSize: '1.2rem' } }}>
                                                    {tab.icon}
                                                </Box>
                                        )}
                                        <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
                                            {tab.translationKey ? t(tab.translationKey) : tab.title}
                                        </Typography>

                                        {tab.closable && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleClose(e, tab.path)}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                sx={{
                                                    p: 0.5,
                                                    ml: 1,
                                                    opacity: 0.6,
                                                    '&:hover': { opacity: 1, color: 'error.main' }
                                                }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Tabs
                        value={activeTabPath}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="navigation tabs"
                        sx={{
                            flexGrow: 1,
                            '& .MuiTab-root': {
                                minHeight: 48,
                                textTransform: 'none',
                                fontWeight: 500,
                                minWidth: 'auto',
                                px: 0.75,
                            }
                        }}
                    >
                        {tabs.map((tab) => (
                            <Tab
                                key={tab.path}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {tab.icon && (
                                            typeof tab.icon === 'string' ?
                                                <i className={tab.icon} style={{ fontSize: '1.2rem', marginRight: '4px' }}></i> :
                                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', '& > svg': { fontSize: '1.2rem' } }}>
                                                    {tab.icon}
                                                </Box>
                                        )}
                                        {tab.translationKey ? t(tab.translationKey) : tab.title}
                                        {tab.closable && (
                                            <IconButton
                                                size="small"
                                                component="span"
                                                onClick={(e) => handleClose(e, tab.path)}
                                                sx={{
                                                    p: 0.5,
                                                    ml: 1,
                                                    opacity: 0.6,
                                                    '&:hover': { opacity: 1, color: 'error.main' }
                                                }}
                                            >
                                                <CloseIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                }
                                value={tab.path}
                            />
                        ))}
                    </Tabs>
                )}

                {/* Right Action Bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, gap: 0.5 }}>
                    {/* Global Fullscreen Toggle */}
                    <Tooltip title={isFullScreen ? (t('common.actions.exitFullScreen') || "Exit Full Screen") : (t('common.actions.enterFullScreen') || "Full Screen")}>
                        <IconButton color="inherit" onClick={handleToggleFullScreen} size="small">
                            {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    </Tooltip>

                    {/* Notifications */}
                    <Tooltip title={t('common.actions.notifications') || "Notifications"}>
                        <IconButton color="inherit" onClick={() => setNotifOpen(true)}>
                            <Badge badgeContent={unreadCount} color="error">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    {/* Chat */}
                    <Tooltip title={t('common.actions.chat') || "Chat"}>
                        <IconButton color="inherit" onClick={() => setChatOpen(true)}>
                            <Badge badgeContent={chatUnreadCount} color="primary">
                                <ChatIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    {/* User Menu */}
                    <Box sx={{ ml: 1 }}>
                        <Tooltip title={t('common.actions.profile') || "Profile"}>
                            <IconButton onClick={handleUserMenuOpen} sx={{ p: 0 }}>
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: 'primary.main',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {user?.name?.charAt(0) || 'U'}
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: '45px' }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleUserMenuClose}
                        >
                            <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
                                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 'bold' }}>
                                    {user?.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BusinessIcon fontSize="inherit" /> {user?.agency}
                                </Typography>
                                {user?.profileLib && (
                                    <Typography variant="body2" color="text.secondary" noWrap sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <AccountCircle fontSize="inherit" /> {user?.profileLib}
                                    </Typography>
                                )}
                                {currentApp && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                                        <AppsIcon sx={{ color: 'primary.main', fontSize: '0.95rem' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                            {currentApp} {currentAppVersion ? `(${currentAppVersion})` : ''}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Divider />

                            {/* Language Submenu Trigger */}
                            <MenuItem onClick={handleLangOpen}>
                                <ListItemIcon>
                                    <LanguageIcon fontSize="small" color="primary" />
                                </ListItemIcon>
                                {t('common.languages') || "Languages"}
                                <Box component="span" sx={{ marginInlineStart: 'auto', fontSize: '0.75rem', fontWeight: 'bold', color: 'text.secondary' }}>
                                    {language.toUpperCase()}
                                </Box>
                            </MenuItem>

                            {/* Default View Mode Submenu Trigger */}
                            <MenuItem onClick={handleViewModeOpen}>
                                <ListItemIcon>
                                    {defaultViewMode === 'grid' ? (
                                        <GridViewIcon fontSize="small" color="primary" />
                                    ) : (
                                        <TableListIcon fontSize="small" color="primary" />
                                    )}
                                </ListItemIcon>
                                {t('common.default_view_mode') || "Default Display"}
                                <Box component="span" sx={{ marginInlineStart: 'auto', fontSize: '0.75rem', fontWeight: 'bold', color: 'text.secondary' }}>
                                    {defaultViewMode === 'grid' ? (t('common.view_grid') || 'Grid') : (t('common.view_table') || 'Table')}
                                </Box>
                            </MenuItem>

                            <MenuItem
                                onClick={handleThemeOpen}
                                sx={{
                                    '&:hover .MuiSvgIcon-root': {
                                        transform: 'rotate(90deg)'
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <PaletteIcon
                                        fontSize="small"
                                        color="primary"
                                        sx={{ transition: 'transform 0.3s ease-in-out' }}
                                    />
                                </ListItemIcon>
                                {t('theme.settings.title') || "Appearance"}
                            </MenuItem>

                            <MenuItem onClick={handleManualOpen}>
                                <ListItemIcon>
                                    <HelpIcon fontSize="small" color="primary" />
                                </ListItemIcon>
                                {t('common.actions.userManual') || "User Manual"}
                            </MenuItem>

                            <Divider />

                            <MenuItem onClick={handleSwitchApp}>
                                <ListItemIcon>
                                    <AppsIcon fontSize="small" color="primary" />
                                </ListItemIcon>
                                {t('common.actions.switchApp') || "Changer d'application"}
                            </MenuItem>

                            <MenuItem onClick={handleLogout}>
                                <ListItemIcon>
                                    <Logout fontSize="small" color="error" />
                                </ListItemIcon>
                                {t('auth.logout') || "Logout"}
                            </MenuItem>
                        </Menu>

                        {/* Language Menu (Relocated) */}
                        <Menu
                            anchorEl={anchorElLang}
                            open={Boolean(anchorElLang)}
                            onClose={handleLangClose}
                            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem onClick={() => handleChangeLanguage('fr')} selected={language === 'fr'}>Français (FR)</MenuItem>
                            <MenuItem onClick={() => handleChangeLanguage('en')} selected={language === 'en'}>English (EN)</MenuItem>
                            <MenuItem onClick={() => handleChangeLanguage('ar')} selected={language === 'ar'}>العربية (AR)</MenuItem>
                        </Menu>

                        {/* View Mode Menu */}
                        <Menu
                            anchorEl={anchorElViewMode}
                            open={Boolean(anchorElViewMode)}
                            onClose={handleViewModeClose}
                            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem onClick={() => handleChangeViewMode('grid')} selected={defaultViewMode === 'grid'}>
                                <ListItemIcon>
                                    <GridViewIcon fontSize="small" color={defaultViewMode === 'grid' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                {t('common.view_grid') || 'Vue Grille'}
                            </MenuItem>
                            <MenuItem onClick={() => handleChangeViewMode('table')} selected={defaultViewMode === 'table'}>
                                <ListItemIcon>
                                    <TableListIcon fontSize="small" color={defaultViewMode === 'table' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                {t('common.view_table') || 'Vue Tableau'}
                            </MenuItem>
                        </Menu>

                        <ThemeCustomizer
                            open={Boolean(themeAnchorEl)}
                            anchorEl={themeAnchorEl}
                            onClose={handleThemeClose}
                        />
                    </Box>
                </Box >
            </Box>


            <NotificationDrawer
                open={notifOpen}
                onClose={() => setNotifOpen(false)}
            />

            <ChatDrawer
                open={chatOpen}
                onClose={() => setChatOpen(false)}
            />

            <UserManual
                open={manualOpen}
                onClose={() => setManualOpen(false)}
            />
        </>
    );
};

export default TopNavigationTabs;
